import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CommercialAddOn, CommercialApiService, CommercialModuleDefinition, CommercialPlan } from './commercial-api.service';

@Component({
  standalone: true,
  selector: 'ain-commercial-addons',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CurrencySelectorComponent
  ],
  template: `
    <ain-page [title]="t('commercial.addons.title')" [description]="t('commercial.addons.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'commercial.addons.help.title'"
        [descriptionKey]="'commercial.addons.help.description'"
        [bulletKeys]="[
          'commercial.addons.help.bullets.module',
          'commercial.addons.help.bullets.projection'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('commercial.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('commercial.errorLoad')" [message]="error() ?? ''" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('commercial.addons.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('commercial.actions.refresh')" (click)="load()"></button>
            <button
              pButton
              type="button"
              severity="secondary"
              [label]="t('commercial.actions.syncProjection')"
              [disabled]="!selectedAddOn() || syncing()"
              (click)="syncSelected()"></button>
          </div>
          @if (info()) {
            <p class="info">{{ info() }}</p>
          }
          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('commercial.addons.empty')" />
          } @else {
            <p-table
              [value]="rows()"
              dataKey="id"
              [paginator]="true"
              [rows]="15"
              selectionMode="single"
              [selection]="selectedAddOn()"
              (selectionChange)="onAddOnSelected($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"></th>
                  <th>{{ t('commercial.addons.fields.code') }}</th>
                  <th>{{ t('commercial.addons.fields.name') }}</th>
                  <th>{{ t('commercial.addons.fields.moduleKey') }}</th>
                  <th>{{ t('commercial.addons.fields.billingMode') }}</th>
                  <th>{{ t('commercial.addons.fields.price') }}</th>
                  <th>{{ t('commercial.addons.fields.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr [pSelectableRow]="row">
                  <td><p-tableRadioButton [value]="row" /></td>
                  <td>{{ row.code }}</td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.moduleKey }}</td>
                  <td>{{ billingModeLabel(row.billingMode) }}</td>
                  <td>{{ row.price }} {{ row.currency }}</td>
                  <td>{{ statusLabel(row.status) }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }

      <p-dialog [header]="t('commercial.addons.create')" [(visible)]="createVisible" [modal]="true" [style]="{ width: '560px' }">
        <div class="form-grid">
          <label>
            <span>{{ t('commercial.addons.fields.code') }}</span>
            <input pInputText [(ngModel)]="createForm.code" />
          </label>
          <label>
            <span>{{ t('commercial.addons.fields.name') }}</span>
            <input pInputText [(ngModel)]="createForm.name" />
          </label>
          <label class="full">
            <span>{{ t('commercial.addons.fields.description') }}</span>
            <input pInputText [(ngModel)]="createForm.description" />
          </label>
          <label>
            <span>{{ t('commercial.addons.fields.moduleKey') }}</span>
            <p-select
              [options]="moduleOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="createForm.moduleKey"
              [showClear]="true"
              appendTo="body" />
          </label>
          <label>
            <span>{{ t('commercial.addons.fields.billingMode') }}</span>
            <p-select
              [options]="billingModeOptions"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="createForm.billingMode"
              appendTo="body">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </label>
          <label>
            <span>{{ t('commercial.addons.fields.price') }}</span>
            <p-inputNumber [(ngModel)]="createForm.price" [minFractionDigits]="2" [maxFractionDigits]="2" />
          </label>
          <label>
            <span>{{ t('commercial.addons.fields.currency') }}</span>
            <ain-currency-selector [(ngModel)]="createForm.currency" />
          </label>
          <label>
            <span>{{ t('commercial.addons.fields.linkedPlan') }}</span>
            <p-select
              [options]="planOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="createForm.linkedPlanId"
              [showClear]="true"
              appendTo="body" />
          </label>
        </div>
        @if (createError()) {
          <p class="error">{{ createError() }}</p>
        }
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveCreate()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid var(--ain-color-border, #e5e7eb); border-radius: 14px; padding: 16px; background: var(--ain-color-surface); }
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
    .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .form-grid label { display: flex; flex-direction: column; gap: 6px; }
    .form-grid label.full { grid-column: 1 / -1; }
    .info { margin: 0 0 12px; color: var(--ain-color-text-muted, #64748b); }
    .error { color: #b91c1c; margin-top: 8px; }
  `]
})
export class CommercialAddonsComponent implements OnInit {
  private readonly api = inject(CommercialApiService);
  private readonly localizationService = inject(LocalizationService);

  readonly rows = signal<CommercialAddOn[]>([]);
  readonly modules = signal<CommercialModuleDefinition[]>([]);
  readonly plans = signal<CommercialPlan[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);
  readonly saving = signal(false);
  readonly syncing = signal(false);
  readonly createError = signal<string | null>(null);

  readonly selectedAddOn = signal<CommercialAddOn | null>(null);
  createVisible = false;
  createForm = {
    code: '',
    name: '',
    description: '',
    moduleKey: '',
    billingMode: 'RecurringAlignedProrated',
    price: 0,
    currency: 'USD',
    linkedPlanId: null as string | null
  };

  readonly billingModeOptions = [
    { value: 'RecurringAlignedProrated', labelKey: 'commercial.addons.billingMode.recurringAlignedProrated' },
    { value: 'RecurringIndependent', labelKey: 'commercial.addons.billingMode.recurringIndependent' },
    { value: 'OneTime', labelKey: 'commercial.addons.billingMode.oneTime' }
  ];

  ngOnInit(): void {
    this.load();
  }

  moduleOptions = signal<Array<{ label: string; value: string }>>([]);
  planOptions = signal<Array<{ label: string; value: string }>>([]);

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listAddOns().subscribe({
      next: items => {
        this.rows.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('commercial.addons.errorLoad'));
        this.loading.set(false);
      }
    });

    this.api.listModules().subscribe({
      next: items => {
        this.modules.set(items);
        this.moduleOptions.set(items.map(item => ({ label: `${item.name} (${item.moduleKey})`, value: item.moduleKey })));
      }
    });

    this.api.listPlans().subscribe({
      next: items => {
        this.plans.set(items);
        this.planOptions.set(items.map(item => ({ label: `${item.name} (${item.code})`, value: item.id })));
      }
    });
  }

  onAddOnSelected(row: CommercialAddOn | CommercialAddOn[] | null): void {
    const selected = Array.isArray(row) ? row[0] ?? null : row;
    this.selectedAddOn.set(selected);
  }

  openCreate(): void {
    this.createForm = {
      code: '',
      name: '',
      description: '',
      moduleKey: this.modules()[0]?.moduleKey ?? '',
      billingMode: 'RecurringAlignedProrated',
      price: 0,
      currency: 'USD',
      linkedPlanId: null
    };
    this.createError.set(null);
    this.createVisible = true;
  }

  saveCreate(): void {
    if (!this.createForm.code.trim() || !this.createForm.name.trim() || !this.createForm.moduleKey) {
      this.createError.set(this.t('commercial.validation.required'));
      return;
    }

    this.saving.set(true);
    this.createError.set(null);
    this.api.createAddOn({
      code: this.createForm.code.trim(),
      name: this.createForm.name.trim(),
      description: this.createForm.description.trim(),
      moduleKey: this.createForm.moduleKey,
      billingMode: this.createForm.billingMode,
      price: this.createForm.price ?? 0,
      currency: this.createForm.currency || 'USD',
      linkedPlanId: this.createForm.linkedPlanId
    }).subscribe({
      next: () => {
        this.saving.set(false);
        this.createVisible = false;
        this.info.set(this.t('commercial.addons.created'));
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.saving.set(false);
        this.createError.set(this.resolveError(err, 'commercial.addons.errorCreate'));
      }
    });
  }

  syncSelected(): void {
    const row = this.selectedAddOn();
    if (!row) {
      return;
    }

    this.syncing.set(true);
    this.info.set(null);
    this.api.syncProjection({ sourceType: 'AddOn', sourceId: row.id }).subscribe({
      next: result => {
        this.syncing.set(false);
        this.info.set(this.t('commercial.projection.synced').replace('{status}', result.projectionStatus));
      },
      error: () => {
        this.syncing.set(false);
        this.info.set(this.t('commercial.projection.error'));
      }
    });
  }

  billingModeLabel(mode: string): string {
    const match = this.billingModeOptions.find(item => item.value === mode);
    return match ? this.t(match.labelKey) : mode;
  }

  statusLabel(status: string): string {
    const key = `commercial.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  private resolveError(error: HttpErrorResponse, fallbackKey: string): string {
    if (error.status === 409) {
      return this.t('commercial.errors.conflict');
    }
    return this.t(fallbackKey);
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
